import React from "react";
import { useTranslation } from "react-i18next";

const testimonials = [
  {
    company: "AL SHAM Ltd",
    quote:
      "HovaStore gives our team a clearer way to manage sales, receipts, and daily store activity without slowing down the counter.",
    focus: "Retail operations",
    metric: "Faster counters",
  },
  {
    company: "NK Holdings",
    quote:
      "The system helps us keep better records and follow what is happening across the business with less manual checking.",
    focus: "Business visibility",
    metric: "Cleaner records",
  },
  {
    company: "JUBA Supermarket",
    quote:
      "For a busy supermarket workflow, HovaStore makes sales, inventory, and receipt handling feel organized and practical.",
    focus: "Supermarket POS",
    metric: "Daily control",
  },
  {
    company: "HIG Investment Ltd",
    quote:
      "HovaStore supports the operational discipline we need: cleaner records, faster access to reports, and easier follow-up.",
    focus: "Reports and controls",
    metric: "Better reporting",
  },
  {
    company: "MIRA Traders",
    quote:
      "The team understood how we work and helped us move from manual tracking to a more reliable POS routine.",
    focus: "Small business",
    metric: "Simple setup",
  },
  {
    company: "KGL Mart",
    quote:
      "HovaStore is practical for the shop floor. Our team can handle receipts, products, and daily numbers with less back-and-forth.",
    focus: "Branch operations",
    metric: "Less admin",
  },
];

const logoNames = [
  "AL SHAM Ltd",
  "NK Holdings",
  "JUBA Supermarket",
  "HIG Investment Ltd",
  "MIRA Traders",
  "KGL Mart",
];

const Testimonials: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden bg-slate-50 px-4 py-20 dark:bg-slate-950 sm:px-6">
      <div className="absolute left-10 top-16 hidden h-24 w-24 rotate-12 rounded-2xl border border-primary/10 bg-primary/5 hs-float lg:block" />
      <div className="absolute bottom-10 right-10 hidden h-28 w-28 rounded-full border border-emerald-300/20 bg-emerald-300/10 hs-soft-pulse lg:block" />

      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 items-end gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary dark:text-pink-200">
              {t("testimonials.eyebrow")}
            </p>
            <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-5xl dark:text-white">
              {t("testimonials.title")}
            </h2>
          </div>
          <p className="text-lg leading-8 text-slate-600 dark:text-slate-300">
            {t("testimonials.description")}
          </p>
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border border-slate-200 bg-white py-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="hs-marquee flex w-[200%] gap-4">
            {[...Array(2)].map((_, groupIndex) => (
              <div key={groupIndex} className="flex min-w-1/2 gap-4 px-2">
                {logoNames.map((name) => (
                  <div
                    key={`${groupIndex}-${name}`}
                    className="flex min-w-max items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-sm font-black text-white">
                      {name.charAt(0)}
                    </span>
                    <span className="text-sm font-black text-slate-700 dark:text-slate-200">
                      {name}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex min-w-full snap-x snap-mandatory gap-5">
            {testimonials.map((item) => (
              <article
                key={item.company}
                className="group flex min-h-[300px] w-[84vw] shrink-0 snap-start flex-col rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-900/5 transition hover:-translate-y-1 hover:border-primary/30 dark:border-slate-800 dark:bg-slate-900 sm:w-[420px]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-lg font-black text-primary transition group-hover:bg-primary group-hover:text-white dark:bg-pink-300/10 dark:text-pink-100">
                      {item.company.charAt(0)}
                    </div>
                    <div>
                      <div className="font-black text-slate-950 dark:text-white">
                        {item.company}
                      </div>
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {item.focus}
                      </div>
                    </div>
                  </div>
                  <div className="hidden rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-500 dark:bg-slate-800 dark:text-slate-400 sm:block">
                    {item.metric}
                  </div>
                </div>
                <p className="mt-5 flex-1 text-sm font-semibold leading-7 text-slate-600 dark:text-slate-300">
                  “{item.quote}”
                </p>
                <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
                  <div>
                    <div className="text-base font-black text-slate-950 dark:text-white">
                      {item.company}
                    </div>
                    <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                      {t("testimonials.customer")}
                    </div>
                  </div>
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-black text-primary dark:bg-pink-300/10 dark:text-pink-100">
                    {item.metric}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
